import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import type { ComplaintCategory, ComplaintCluster, Page, SystemicIssueAnalysis, SystemicIssueStatus } from '../../lib/types';
import { useApiQuery } from '../../lib/useApi';
import { EmptyState, ErrorNote, Field, Modal, PageHeader, RoleNotice, Spinner, StatusBadge, Tabs } from '../../ui/components';

const OMBUDSMAN_ROLES = ['OMBUDSMAN_OFFICER'];
const STATUSES: SystemicIssueStatus[] = ['DRAFT', 'PUBLISHED'];
const CATEGORIES: ComplaintCategory[] = [
  'MALADMINISTRATION',
  'UNREASONABLE_DELAY',
  'DISCOURTESY',
  'UNFAIR_TREATMENT',
  'DENIAL_OF_SERVICE',
  'PROCUREMENT_IRREGULARITY',
  'CORRUPTION_SUSPECTED',
  'OTHER',
];

/** GET /systemic-issues/clusters (live, never stored) + GET/POST /systemic-issues (drafted analyses). */
export function SystemicIssues() {
  const { persona } = useAuth();
  const isOfficer = persona?.roles.includes('OMBUDSMAN_OFFICER') ?? false;
  const navigate = useNavigate();
  const [tab, setTab] = useState<'clusters' | 'analyses'>('clusters');
  const [showCreate, setShowCreate] = useState<ComplaintCluster | null | 'blank'>(null);

  return (
    <div>
      <PageHeader
        title="Systemic Issues"
        subtitle="Complaint-pattern clustering by MDA/category — a live suggestion, formalised into a published analysis when warranted."
      />
      {!isOfficer ? <RoleNotice roles={OMBUDSMAN_ROLES} /> : null}
      {isOfficer ? (
        <>
          <Tabs tabs={[{ key: 'clusters', label: 'Live clusters' }, { key: 'analyses', label: 'Analyses' }]} value={tab} onChange={setTab} />
          {tab === 'clusters' ? <ClustersPanel onDraft={(cluster) => setShowCreate(cluster)} /> : <AnalysesPanel onCreate={() => setShowCreate('blank')} navigate={navigate} />}
        </>
      ) : null}
      {showCreate ? (
        <CreateAnalysisModal
          fromCluster={showCreate === 'blank' ? null : showCreate}
          onClose={() => setShowCreate(null)}
          onDone={(created) => {
            setShowCreate(null);
            navigate(`/ombudsman/systemic-issues/${created.id}`);
          }}
        />
      ) : null}
    </div>
  );
}

function ClustersPanel({ onDraft }: { onDraft: (cluster: ComplaintCluster) => void }) {
  const [subjectMda, setSubjectMda] = useState('');
  const [minCount, setMinCount] = useState('3');
  const qp = new URLSearchParams();
  if (subjectMda.trim()) qp.set('subjectMda', subjectMda.trim());
  if (minCount.trim()) qp.set('minCount', minCount.trim());
  const clustersQ = useApiQuery<{ since: string; items: ComplaintCluster[] }>(`/api/v1/m40/systemic-issues/clusters?${qp.toString()}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end gap-3">
        <Field label="Subject MDA (optional)" className="min-w-[220px] flex-1">
          <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} />
        </Field>
        <Field label="Minimum count" className="!mb-0">
          <input type="number" className="field-input !w-24" value={minCount} onChange={(e) => setMinCount(e.target.value)} />
        </Field>
      </div>
      {clustersQ.error ? <ErrorNote message={clustersQ.error} /> : null}
      {clustersQ.loading ? (
        <Spinner />
      ) : (clustersQ.data?.items.length ?? 0) === 0 ? (
        <EmptyState title="No clusters meet this threshold" hint={`Since ${clustersQ.data?.since ? new Date(clustersQ.data.since).toLocaleDateString() : '—'}`} />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Subject MDA</th>
                <th className="table-head">Category</th>
                <th className="table-head">Count</th>
                <th className="table-head">Sample refs</th>
                <th className="table-head" />
              </tr>
            </thead>
            <tbody>
              {clustersQ.data!.items.map((c, i) => (
                <tr key={`${c.subjectMda}-${c.category}-${i}`}>
                  <td className="table-cell font-medium">{c.subjectMda}</td>
                  <td className="table-cell text-xs">{c.category.replaceAll('_', ' ')}</td>
                  <td className="table-cell text-xs tabular-nums">{c.count}</td>
                  <td className="table-cell font-mono text-[11px] text-slate-400">{c.sampleComplaintRefs.join(', ')}</td>
                  <td className="table-cell text-right">
                    <button className="btn-secondary !px-2.5 !py-1 text-xs" onClick={() => onDraft(c)}>
                      Draft analysis
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function AnalysesPanel({ onCreate, navigate }: { onCreate: () => void; navigate: (path: string) => void }) {
  const [status, setStatus] = useState<SystemicIssueStatus | ''>('');
  const qp = new URLSearchParams({ limit: '50' });
  if (status) qp.set('status', status);
  const listQ = useApiQuery<Page<SystemicIssueAnalysis>>(`/api/v1/m40/systemic-issues?${qp.toString()}`);

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <Field label="Status" className="!mb-0">
          <select className="field-input !w-auto" value={status} onChange={(e) => setStatus(e.target.value as SystemicIssueStatus | '')}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>
        <button className="btn-primary" onClick={onCreate}>
          + Draft analysis
        </button>
      </div>
      {listQ.error ? <ErrorNote message={listQ.error} /> : null}
      <div className="card overflow-x-auto">
        {listQ.loading ? (
          <Spinner />
        ) : (listQ.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="No analyses drafted yet" />
        ) : (
          <table className="w-full">
            <thead>
              <tr>
                <th className="table-head">Ref</th>
                <th className="table-head">Subject MDA</th>
                <th className="table-head">Complaint count</th>
                <th className="table-head">Status</th>
              </tr>
            </thead>
            <tbody>
              {listQ.data!.items.map((a) => (
                <tr key={a.id} className="cursor-pointer hover:bg-slate-50" onClick={() => navigate(`/ombudsman/systemic-issues/${a.id}`)}>
                  <td className="table-cell font-mono text-xs">{a.refNo}</td>
                  <td className="table-cell font-medium">{a.subjectMda}</td>
                  <td className="table-cell text-xs tabular-nums">{a.complaintCount}</td>
                  <td className="table-cell">
                    <StatusBadge status={a.status} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function CreateAnalysisModal({ fromCluster, onClose, onDone }: { fromCluster: ComplaintCluster | null; onClose: () => void; onDone: (a: SystemicIssueAnalysis) => void }) {
  const [subjectMda, setSubjectMda] = useState(fromCluster?.subjectMda ?? '');
  const [category, setCategory] = useState<ComplaintCategory | ''>((fromCluster?.category as ComplaintCategory) ?? '');
  const [periodStart, setPeriodStart] = useState('');
  const [periodEnd, setPeriodEnd] = useState('');
  const [complaintCount, setComplaintCount] = useState(String(fromCluster?.count ?? ''));
  const [findings, setFindings] = useState('');
  const [recommendations, setRecommendations] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.post<SystemicIssueAnalysis>('/api/v1/m40/systemic-issues', {
        subjectMda,
        category: category || undefined,
        periodStart,
        periodEnd,
        complaintCount: Number.parseInt(complaintCount, 10),
        sampleComplaintRefs: fromCluster?.sampleComplaintRefs ?? [],
        findings,
        recommendations,
      });
      onDone(created);
    } catch (e) {
      setError(e instanceof ApiError ? `${e.message} (${e.code})` : 'Failed to draft analysis');
    } finally {
      setBusy(false);
    }
  };

  const valid =
    subjectMda.trim().length >= 2 &&
    /^\d{4}-\d{2}-\d{2}$/.test(periodStart) &&
    /^\d{4}-\d{2}-\d{2}$/.test(periodEnd) &&
    Number.isFinite(Number(complaintCount)) &&
    findings.trim().length >= 20 &&
    recommendations.trim().length >= 20;

  return (
    <Modal title="Draft a systemic issue analysis" onClose={onClose} wide>
      <div className="space-y-3">
        {error ? <ErrorNote message={error} /> : null}
        <div className="grid grid-cols-2 gap-3">
          <Field label="Subject MDA">
            <input className="field-input" value={subjectMda} onChange={(e) => setSubjectMda(e.target.value)} />
          </Field>
          <Field label="Category (optional)">
            <select className="field-input" value={category} onChange={(e) => setCategory(e.target.value as ComplaintCategory | '')}>
              <option value="">Any</option>
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c.replaceAll('_', ' ')}
                </option>
              ))}
            </select>
          </Field>
        </div>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Period start">
            <input type="date" className="field-input" value={periodStart} onChange={(e) => setPeriodStart(e.target.value)} />
          </Field>
          <Field label="Period end">
            <input type="date" className="field-input" value={periodEnd} onChange={(e) => setPeriodEnd(e.target.value)} />
          </Field>
          <Field label="True complaint count (from the cluster query)">
            <input type="number" className="field-input" value={complaintCount} onChange={(e) => setComplaintCount(e.target.value)} />
          </Field>
        </div>
        <Field label="Findings">
          <textarea className="field-input" rows={3} value={findings} onChange={(e) => setFindings(e.target.value)} />
        </Field>
        <Field label="Recommendations">
          <textarea className="field-input" rows={3} value={recommendations} onChange={(e) => setRecommendations(e.target.value)} />
        </Field>
        <div className="flex justify-end gap-2 pt-2">
          <button className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn-primary" onClick={submit} disabled={busy || !valid}>
            {busy ? 'Drafting…' : 'Draft analysis'}
          </button>
        </div>
      </div>
    </Modal>
  );
}
